export type CompactMetricValueResult = {
  displayValue: string;
  tooltipValue?: string;
};

const COMPACT_NUMBER_SUFFIXES = [
  {value: 1_000_000_000_000, suffix: 'T'},
  {value: 1_000_000_000, suffix: 'B'},
  {value: 1_000_000, suffix: 'M'},
  {value: 1_000, suffix: 'k'},
] as const;

const COMPACT_NUMBER_SUFFIX_MULTIPLIERS: Record<string, number> = {
  k: 1_000,
  m: 1_000_000,
  b: 1_000_000_000,
  t: 1_000_000_000_000,
};

/** Matches workstream metrics: null/empty values render as "-". */
export const getMetricDisplayValue = (value?: string | null): string =>
  value === '' || value == null ? '-' : value;

/**
 * Currency string with a suffix derived from the value's actual magnitude
 * (k/M/B/T), e.g. `-2_400_000` -> `"-$2.4M"`. Values below 1,000 keep up to two
 * decimals and no suffix. Use this instead of trusting a metric's unit string.
 */
export const formatCompactCurrency = (value: number): string => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);

  const suffixEntry = COMPACT_NUMBER_SUFFIXES.find((candidate) => abs >= candidate.value);
  if (!suffixEntry) {
    return `${sign}$${abs.toLocaleString('en-US', {maximumFractionDigits: 2})}`;
  }

  const scaledValue = abs / suffixEntry.value;
  const decimalPlaces = Number.isInteger(scaledValue) || scaledValue >= 100 ? 0 : 1;
  const roundedScaledValue = scaledValue.toFixed(decimalPlaces).replace(/\.0$/, '');
  return `${sign}$${roundedScaledValue}${suffixEntry.suffix}`;
};

const parseMetricValueParts = (trimmedValue: string) => {
  const partsMatch = /^([^0-9+.-]*)([+-]?(?:\d[\d,]*(?:\.\d+)?|\.\d+))([^0-9]*)$/.exec(
    trimmedValue
  );
  if (!partsMatch) {
    return null;
  }

  const [, , numericPortion, suffix] = partsMatch;
  const normalizedNumericPortion = numericPortion.replace(/,/g, '');
  const suffixScaleMatch = /^([kKmMbBtT])([^0-9]*)$/.exec(suffix);
  const scaleMultiplier = suffixScaleMatch
    ? (COMPACT_NUMBER_SUFFIX_MULTIPLIERS[suffixScaleMatch[1].toLowerCase()] ?? 1)
    : 1;
  const parsedValue = Number(normalizedNumericPortion) * scaleMultiplier;

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

// Accept both -$5 and $-5, Unicode minus signs, and accounting negatives.
const normalizeMetricNumber = (value: string): string => {
  let normalized = value.replace(/[−–]/g, '-');
  const accounting = /^\((.*)\)$/.exec(normalized);
  if (accounting) normalized = `-${accounting[1]}`;
  return normalized.replace(/^([+-])([^0-9+.-]+)(?=\d|\.\d)/, '$2$1');
};

/** Best-effort numeric parse for charts (e.g. scorecard target lines). */
export const parseMetricValueString = (rawValue?: string | null): number | null => {
  const trimmedValue = rawValue == null ? undefined : normalizeMetricNumber(rawValue.trim());
  if (!trimmedValue || trimmedValue === '-') {
    return null;
  }

  const alreadyCompactMatch = /^([^0-9+.-]*)([+-]?\d+(?:\.\d+)?)([kKmMbBtT])([^0-9]*)$/.exec(
    trimmedValue
  );
  if (alreadyCompactMatch) {
    return parseMetricValueParts(trimmedValue);
  }

  return parseMetricValueParts(trimmedValue);
};

export const formatCompactMetricValue = (rawValue: string): CompactMetricValueResult => {
  const trimmedValue = normalizeMetricNumber(rawValue.trim());
  if (!trimmedValue || trimmedValue === '-') {
    return {displayValue: rawValue, tooltipValue: undefined};
  }

  const alreadyCompactMatch = /^([^0-9+.-]*)([+-]?\d+(?:\.\d+)?)([kKmMbBtT])([^0-9]*)$/.exec(
    trimmedValue
  );
  if (alreadyCompactMatch) {
    const compactMantissa = Number(alreadyCompactMatch[2]);
    if (Number.isFinite(compactMantissa) && Math.abs(compactMantissa) < 1_000) {
      return {displayValue: rawValue, tooltipValue: undefined};
    }
  }

  const parsedValue = parseMetricValueParts(trimmedValue);
  if (parsedValue == null || Math.abs(parsedValue) < 1_000) {
    return {displayValue: rawValue, tooltipValue: undefined};
  }

  const partsMatch = /^([^0-9+.-]*)([+-]?(?:\d[\d,]*(?:\.\d+)?|\.\d+))([^0-9]*)$/.exec(
    trimmedValue
  );
  if (!partsMatch) {
    return {displayValue: rawValue, tooltipValue: undefined};
  }

  const [, prefix, , suffix] = partsMatch;
  const suffixScaleMatch = /^([kKmMbBtT])([^0-9]*)$/.exec(suffix);
  const suffixRemainder = suffixScaleMatch ? suffixScaleMatch[2] : suffix;

  const compactSuffix = COMPACT_NUMBER_SUFFIXES.find(
    (candidate) => Math.abs(parsedValue) >= candidate.value
  );
  if (!compactSuffix) {
    return {displayValue: rawValue, tooltipValue: undefined};
  }

  const scaledValue = parsedValue / compactSuffix.value;
  const decimalPlaces = Number.isInteger(scaledValue) || Math.abs(scaledValue) >= 100 ? 0 : 1;
  const roundedScaledValue = scaledValue.toFixed(decimalPlaces).replace(/\.0$/, '');
  const compactValue = `${prefix}${roundedScaledValue}${compactSuffix.suffix}${suffixRemainder}`;

  return {
    displayValue: compactValue,
    tooltipValue: compactValue === trimmedValue ? undefined : rawValue,
  };
};
